import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { fetchOpcos, type Opco } from '../api/platform.js';

// Active-OPCO context. Loads the OPCO list once on app boot, surfaces the
// current selection + setter, persists selection in localStorage so reloads
// preserve user choice.
//
// SFC is excluded from the selectable list — it's the parent holding co, not
// an operating business that books vendors/orders. (sfc-shared-db has it as
// a row in operating_businesses but Anvil scopes by actual OPCO.)

const LS_KEY = 'anvil.activeOpco';
const HOLDING_CO_SHORT_CODE = 'SFC';

interface OpcoContextValue {
  readonly opcos: ReadonlyArray<Opco>;
  readonly active: Opco | null;
  readonly setActive: (shortCode: string) => void;
  readonly loading: boolean;
  readonly error: string | null;
}

const OpcoContext = createContext<OpcoContextValue | null>(null);

export function OpcoProvider({ children }: { readonly children: ReactNode }) {
  const [opcos, setOpcos] = useState<ReadonlyArray<Opco>>([]);
  const [activeShortCode, setActiveShortCode] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LS_KEY);
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchOpcos();
        if (cancelled) return;
        // Filter out SFC (holding co) from the operational OPCO list.
        const filtered = rows.filter((o) => o.shortCode !== HOLDING_CO_SHORT_CODE);
        setOpcos(filtered);
        // If no persisted selection or it's no longer in the list, default to
        // the first active OPCO.
        setActiveShortCode((prev) => {
          if (prev && filtered.some((o) => o.shortCode === prev)) return prev;
          return filtered[0]?.shortCode ?? null;
        });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setActive = useCallback((shortCode: string) => {
    setActiveShortCode(shortCode);
    try {
      localStorage.setItem(LS_KEY, shortCode);
    } catch {
      // localStorage may be unavailable in private mode — silently fall back
      // to in-memory state.
    }
  }, []);

  const value = useMemo<OpcoContextValue>(() => {
    const active = opcos.find((o) => o.shortCode === activeShortCode) ?? null;
    return { opcos, active, setActive, loading, error };
  }, [opcos, activeShortCode, setActive, loading, error]);

  return <OpcoContext.Provider value={value}>{children}</OpcoContext.Provider>;
}

export function useOpco(): OpcoContextValue {
  const ctx = useContext(OpcoContext);
  if (!ctx) throw new Error('useOpco must be called inside <OpcoProvider>');
  return ctx;
}
